import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import {
	getOutgoingShareList,
	revokeShare,
	resendShare,
} from "../actions/shareActions";
import { openValueCopyDialog } from "../actions/globalEditDialogActions";
import CustomizableTable from "./Tables/CustomizableTable";
import InteractiveTableCell from "./Tables/InteractiveTableCell";
import { mediumDateFormat, pluralizeWithNum } from "../utilities/utilities";
import {
	getActionDisableMessage,
	getResendButtonText,
} from "./ExternalSharingUtils";
import { DropDownButtonWithRouter } from "./DropDownButton";
import { LexWorkplaceGavelIcon } from "./LexWorkplaceIcons";
import { ID_COLUMN_WIDTH } from "./Tables/SortableTable";

const tableUniqueID = "/sharing/outgoing";

class ShareList extends Component {
	constructor(props, context) {
		super(props, context);
		this.getColumns = this.getColumns.bind(this);
		this.makeThreeDotMenuItems = (row) => {
			const disableDownloadLink = getActionDisableMessage({
				canSeeAllShares: this.props.canSeeAllShares,
				cognitoSub: this.props.cognitoSub,
				record: row.original,
				userAction: "copyDownloadLink",
			});
			var menu = [
				{
					text: "View Share Info",
					href: `/sharing/outgoing/${row.original.id}`,
				},
				{
					text: "View Share Download Link",
					onSelect: () => {
						const downloadURL = row.original.downloadURL;
						this.props.openValueCopyDialog(
							downloadURL,
							"Share Download Link",
							"Download Link Copied to Clipboard"
						);
					},
					disabled: disableDownloadLink !== undefined,
					disabledHoverText: disableDownloadLink || "",
				},
			];

			if (
				row.original.displayStatus !== "Processing" &&
				row.original.displayStatus !== "Expired"
			) {
				const disableResend = getActionDisableMessage({
					canSeeAllShares: this.props.canSeeAllShares,
					cognitoSub: this.props.cognitoSub,
					record: row.original,
					userAction: "resend",
				});
				menu.push({
					text: getResendButtonText(
						row.original,
						"Resend Share Email"
					),
					disabled: disableResend !== undefined,
					disabledHoverText: disableResend || "",
					onSelect: () =>
						this.props.resendShare(
							row.original.id,
							"ExternalSharingManagement",
							this.props.matterIDFilter
						),
				});
			}

			if (row.original.displayStatus === "Active") {
				const disableRevoke = getActionDisableMessage({
					canSeeAllShares: this.props.canSeeAllShares,
					cognitoSub: this.props.cognitoSub,
					record: row.original,
					userAction: "revoke",
				});
				menu.push({
					text: "Revoke Share",
					disabled: disableRevoke !== undefined,
					disabledHoverText: disableRevoke || "",
					onSelect: () =>
						this.props.revokeShare(
							row.original.id,
							"ExternalSharingManagement",
							this.props.matterIDFilter
						),
				});
			}

			return menu;
		};

		this.filterMap = {
			All: () => true,
			Active: (row) =>
				row.displayStatus === "Active" ||
				row.displayStatus === "Processing",
			Expired: (row) => row.displayStatus === "Expired",
			Revoked: (row) => row.displayStatus === "Revoked",
			Failed: (row) =>
				row.displayStatus === "Failed" ||
				row.displayStatus === "Never Finished",
		};
	}
	getColumns(includeMatterName) {
		var columns = [
			{ Header: "ID", accessor: "id", width: ID_COLUMN_WIDTH },
			{ Header: "Sender", accessor: "createdByDisplayName" },
			{
				Header: "Recipients",
				accessor: "recipients",
				Cell: (row) => (
					<InteractiveTableCell
						menuItems={this.makeThreeDotMenuItems(row)}
					>
						{row.value ? row.value.join(", ") : ""}
					</InteractiveTableCell>
				),
			},
			{
				Header: "Sent",
				accessor: "lastEmailSent",
				Cell: (row) =>
					row.value ? (
						mediumDateFormat(row.value)
					) : row.original.status === "Processing" ? (
						<span
							style={{ fontFamily: "MainFont-Italic" }}
							title="Email not sent yet, still processing"
						>
							{mediumDateFormat(row.original.createdAt) + "*"}
						</span>
					) : (
						""
					),
			},
			{
				Header: "Documents Sent",
				accessor: "totalObjects",
				Cell: (row) => (
					<Link to={`/sharing/outgoing/${row.original.id}`}>
						{row.value === null
							? ""
							: pluralizeWithNum("Document", row.value)}
					</Link>
				),
			},
			{
				Header: "Status",
				accessor: "displayStatus",
				Cell: (row) =>
					row.original.lastEmailFailed && row.value === "Active" ? (
						<span
							style={{ fontFamily: "MainFont-Italic" }}
							title="Last email failed to send"
						>
							{row.value + "*"}
						</span>
					) : (
						<span>{row.value}</span>
					),
			},
		];
		if (includeMatterName) {
			columns.push({
				Header: "Matter",
				accessor: "matterName",
				Cell: (row) => {
					return row.original.matterWasPermanentlyDeleted ? (
						// If the matter was permanently deleted, show the name without a link
						// matterName was set on the backend
						<>{row.original.matterName}</>
					) : (
						<Link to={row.original.matterLink}>
							{row.original.matterName}
						</Link>
					);
				},
			});
		}
		columns = columns.concat([
			{
				Header: "Expires",
				accessor: "expiresAt",
				Cell: (row) => (row.value ? mediumDateFormat(row.value) : ""),
			},
			{
				Header: "D/L",
				accessor: "hasBeenDownloaded",
				width: 40,
				filterable: false,
				csvColumnName: "Downloaded",
				Cell: (row) => (row.value ? "✓" : ""),
			},
		]);

		return columns;
	}
	componentDidMount() {
		this.props.getOutgoingShareList(this.props.matterIDFilter);
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.matterLifecycleStatus !== this.props.matterLifecycleStatus
		) {
			this.props.getOutgoingShareList(this.props.matterIDFilter);
		}
	}

	render() {
		return (
			<div className="fullHeightFlex noDefaultMargin">
				<CustomizableTable
					actionButtons={({ openCsvExport }) => (
						<DropDownButtonWithRouter
							title="Actions"
							iconComponent={<LexWorkplaceGavelIcon />}
							pullRight
							menuItems={[
								{
									text: "Export Data as CSV",
									onSelect: openCsvExport,
								},
							]}
						/>
					)}
					tabsFilterMap={this.filterMap}
					defaultTabFilterValue="Active"
					className="fullWidth"
					loading={this.props.loading}
					data={this.props.outgoingShareList}
					dataIsStale={this.props.outgoingShareListStale}
					autoResize
					columns={this.getColumns(this.props.includeMatterColumn)}
					noResultsText={"No Outgoing Shares to show"}
					defaultSorted={[{ id: "lastEmailSent", desc: true }]}
					tableUniqueID={tableUniqueID}
					csvExport={{
						csvColumnRenderers: {
							recipients: (val) => (val || []).join("; "),
							lastEmailSent: (val) =>
								val ? mediumDateFormat(val) : "",
							expiresAt: (val) =>
								val ? mediumDateFormat(val) : "",
							hasBeenDownloaded: (val) => (val ? "Yes" : "No"),
						},
						fileName: "shares-export",
					}}
				/>
			</div>
		);
	}
}
ShareList.propTypes = {
	loading: PropTypes.bool.isRequired,
};
const mapStateToProps = (state, ownProps) => ({
	outgoingShareList: state.share.outgoingShareList,
	outgoingShareListStale: state.share.outgoingShareListStale,
	myUserID: state.user.userID,
	loading: state.request.loading,
	cognitoSub: state.user.cognitoUser.attributes.sub,
	hasPermissionsAccess: state.user.hasPermissionsAccess,
	matterIDFilter: ownProps.matterIDFilter,
	canSeeAllShares: state.user.userRights.CAN_SEE_ALL_SHARES,
});
const ExternalSharingManagementBase = connect(mapStateToProps, {
	getOutgoingShareList,
	revokeShare,
	resendShare,
	openValueCopyDialog,
})(ShareList);

class ExternalSharingManagementForMatterUnconnected extends Component {
	render() {
		return (
			<ExternalSharingManagementBase
				includeMatterColumn={false}
				matterIDFilter={this.props.matterIDFilter}
				matterLifecycleStatus={this.props.matterLifecycleStatus}
				canSeeAllShares={this.props.canSeeAllShares}
				cognitoSub={this.props.cognitoSub}
			/>
		);
	}
}
const ExternalSharingManagementForMatter = connect((state, ownProps) => ({
	matterIDFilter: ownProps.matterIDFilter,
}))(ExternalSharingManagementForMatterUnconnected);

class ExternalSharingManagementFullPageUnconnected extends Component {
	render() {
		return <ExternalSharingManagementBase includeMatterColumn={true} />;
	}
}
const ExternalSharingManagementFullPage = connect(
	null,
	{}
)(ExternalSharingManagementFullPageUnconnected);

export {
	ExternalSharingManagementForMatter,
	ExternalSharingManagementFullPage,
};
